import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { toDate } from '@/lib/date-display.js';
import { KIND_LABELS, lineageLine } from './lineage.js';

// The terms chain, current first (CLIENT-RECORD-SPEC §5 item 2; canvas v2):
// only the open term shows in full; expired terms are ONE collapsed line,
// opened on demand. A term row's primary destination is the deal IN TRACON
// (t.href); "Open in CRM" stays as the secondary icon. Replaces TermsPanel.

function fmtMoney(n) { if (n == null || n === '') return '\u2014'; return '$' + Number(n).toLocaleString('en-US'); }
function fmtDate(iso) { return iso ? String(iso).slice(0, 10) : null; }
function termRange(t) {
  const s = fmtDate(t.policy_start); const e = fmtDate(t.policy_end);
  if (s && e) return `${s} → ${e}`; if (e) return `expires ${e}`; if (s) return `${s} →`; return null;
}

export function TermRow({ t, byId, current, work }) {
  const range = termRange(t);
  const name = t.carrier || (current ? 'no carrier yet' : '(no carrier)');
  const kind = t.kind ? KIND_LABELS[t.kind] : null;
  const lineage = lineageLine(t, byId);
  return (
    <div className={`flex flex-col gap-1 border-b border-border py-2.5 last:border-b-0 ${current ? 'text-[15px]' : ''}`} data-term={t.deal_id}>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          {current ? <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-success" aria-label="open term" /> : null}
          {t.href ? (
            <Link to={t.href} className={`truncate font-medium hover:text-accent hover:underline ${t.carrier ? 'text-text' : 'text-text-dim'}`} aria-label="Open this term in TRACON">{name}</Link>
          ) : <span className="truncate font-medium text-text">{name}</span>}
          {kind ? <span className="shrink-0 rounded border border-border px-1.5 text-[10px] uppercase tracking-wide text-text-muted" data-kind={t.kind}>{kind}</span> : null}
          {t.stage ? <span className="shrink-0 text-[10px] uppercase tracking-wide text-text-dim">{t.stage}</span> : null}
        </div>
        <div className="flex items-baseline gap-3 font-mono text-xs text-text-muted">
          {range ? <span>{range}</span> : null}
          {t.policy_number ? <span className="text-text-dim">#{t.policy_number}</span> : null}
          <span className={t.premium != null ? 'font-semibold text-text' : 'text-text-dim'}>{fmtMoney(t.premium)}</span>
          {t.crm_url ? (
            <a href={t.crm_url} target="_blank" rel="noopener noreferrer" className="text-text-dim hover:text-text" aria-label="Open in CRM"><ExternalLink size={12} strokeWidth={1.7} /></a>
          ) : null}
        </div>
      </div>
      {lineage ? <div className="text-[11px] text-text-dim" data-lineage>{lineage}</div> : null}
      {current && work && work.length ? (
        <div className="text-xs text-text-muted" data-owed>
          Owed now: {work.map((w, i) => <span key={w.code + i}>{i > 0 ? ' · ' : ''}<b className={w.fact_ref ? 'font-medium text-text' : 'font-medium text-text-muted'}>{w.message}</b></span>)}
        </div>
      ) : null}
    </div>
  );
}

function lapseLine(current, byId) {
  const prior = current?.follows ? byId.get(String(current.follows)) : null;
  if (!prior || !prior.policy_end || !current.policy_start) return null;
  const end = toDate(prior.policy_end); const start = toDate(current.policy_start);
  if (!end || !start) return null;
  const months = Math.round((start - end) / (30.44 * 86400000));
  if (!Number.isFinite(months) || months < 1) return null;
  return `Lapsed ${months} month${months === 1 ? '' : 's'} after ${prior.carrier || 'the prior policy'} ended ${String(prior.policy_end).slice(0, 10)}.${current.kind === 'reup' ? ' That is why this is a re-up, not a renewal.' : ''}`;
}

export default function TermsChain({ terms, work }) {
  const [open, setOpen] = useState(false);
  const current = terms?.current || [];
  const historical = terms?.historical || [];
  const byId = new Map([...current, ...historical].map((t) => [String(t.deal_id), t]));
  const carriers = [...new Set(historical.map((t) => t.carrier).filter(Boolean))];
  const years = historical.map((t) => t.policy_end).filter(Boolean).map((d) => String(d).slice(0, 4)).sort();
  const first = current[0] || null;
  const lapse = first ? lapseLine(first, byId) : null;
  return (
    <section className="rounded-card border border-border bg-surface" data-terms-chain>
      <header className="flex items-baseline justify-between border-b border-border px-4 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Current term</h2>
        <span className="font-mono text-[10px] text-text-dim">{current.length} open · {historical.length} expired</span>
      </header>
      <div className="px-4">
        {current.length ? current.map((t, i) => <TermRow key={t.deal_id} t={t} byId={byId} current work={i === 0 ? work : null} />) : (
          <p className="py-3 text-xs italic text-text-dim">No in-force or in-progress policy.</p>
        )}
        {lapse ? <div className="border-t border-border py-2 text-xs text-warning" data-lapse>{lapse}</div> : null}
        {historical.length ? (
          <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between border-t border-border py-2.5 text-left text-xs text-text-muted hover:text-text" aria-expanded={open} data-expired-toggle>
            <span>{historical.length} expired term{historical.length === 1 ? '' : 's'}{years.length ? ` · ${years[0]} to ${years[years.length - 1]}` : ''}{carriers.length ? ` · ${carriers.join(', ')}` : ''}</span>
            <span>{open ? '▾' : '▸'}</span>
          </button>
        ) : null}
        {open ? historical.map((t) => <TermRow key={t.deal_id} t={t} byId={byId} />) : null}
      </div>
    </section>
  );
}
